import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Search, ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

// Primary bar: Home | News | Events | Food & Drink | Property | Roads |
// Incident Watch | Discover. Items marked `collapse` drop out of the bar below
// 1280px and reappear inside Discover, so nothing is ever squeezed or lost.
const PRIMARY = [
  { name: 'Home', url: '/' },
  { name: 'News', url: '/news' },
  { name: 'Events', url: '/events' },
  { name: 'Food & Drink', url: '/food-drink', collapse: true },
  {
    name: 'Property',
    collapse: true,
    dropdown: [
      { name: 'To Let', url: '/properties' },
      { name: 'For Sale', url: '/for-sale' },
    ],
  },
  { name: 'Roads', url: '/roads-and-travel' },
  { name: 'Incident Watch', url: '/incident-watch', accent: true },
];

// Secondary sections live here. Directory stays on the sister site.
const DISCOVER = [
  { name: 'St Mirren', url: '/st-mirren' },
  { name: 'Visitor Attractions', url: '/attractions' },
  { name: 'Paisley History', url: '/history' },
  { name: 'Famous People', url: '/famous-people' },
  { name: 'Jobs', url: '/jobs' },
  { name: 'Community', url: '/community' },
  { name: 'Useful Information', url: '/useful-information' },
  { name: 'Directory', url: 'https://paisleyscotland.com/directory', external: true },
];

const COLLAPSED = PRIMARY.filter((l) => l.collapse).flatMap((l) =>
  l.dropdown ? l.dropdown : [{ name: l.name, url: l.url }]
);

const BAR_LINK =
  'px-3 py-1.5 text-[13px] font-semibold whitespace-nowrap rounded-full transition-colors duration-200';
const BAR_DEFAULT = `${BAR_LINK} text-slate-700 hover:text-slate-900 hover:bg-white/50`;
// Incident Watch: quietly distinct via the product's own sky accent — a live
// dot and a tinted pill. Deliberately not red, not siren-like.
const BAR_ACCENT = `${BAR_LINK} text-sky-800 bg-sky-50/70 ring-1 ring-sky-200/70 hover:bg-sky-100/80 hover:text-sky-900 inline-flex items-center gap-1.5`;

const DROP_ITEM =
  'block px-4 py-2 text-sm font-semibold text-slate-700 hover:text-slate-900 hover:bg-white/60 transition-colors whitespace-nowrap';
const MOBILE_ITEM =
  'block px-3 py-2 text-sm font-semibold text-slate-700 hover:text-slate-900 hover:bg-white/60 rounded-xl transition-all';

const testId = (name) => `nav-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const LiveDot = () => (
  <span className="relative flex h-1.5 w-1.5 shrink-0" aria-hidden>
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-70" />
    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-sky-500" />
  </span>
);

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const closeAll = () => {
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const renderBarItem = (link) => {
    const hideClass = link.collapse ? 'hidden xl:block' : '';

    if (link.dropdown) {
      const isOpen = openDropdown === link.name;
      return (
        <div key={link.name} className={`relative ${hideClass}`}>
          <button
            onClick={() => setOpenDropdown(isOpen ? null : link.name)}
            className={`${BAR_DEFAULT} inline-flex items-center gap-1`}
            data-testid={`${testId(link.name)}-toggle`}
            aria-expanded={isOpen}
          >
            {link.name}
            <ChevronDown size={12} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          {isOpen && (
            <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-2xl py-2 shadow-[0_16px_48px_rgba(15,23,42,0.16)] ring-1 ring-slate-200 z-50">
              {link.dropdown.map((sub) => (
                <Link key={sub.url} to={sub.url} onClick={closeAll} className={DROP_ITEM} data-testid={testId(sub.name)}>
                  {sub.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (link.accent) {
      return (
        <Link key={link.url} to={link.url} className={`${BAR_ACCENT} ${hideClass}`} data-testid={testId(link.name)}>
          <LiveDot />
          {link.name}
        </Link>
      );
    }

    return (
      <Link key={link.url} to={link.url} className={`${BAR_DEFAULT} ${hideClass}`} data-testid={testId(link.name)}>
        {link.name}
      </Link>
    );
  };

  const discoverOpen = openDropdown === 'Discover';

  return (
    <header
      className={`fixed top-3 left-3 right-3 md:top-4 md:left-1/2 md:right-auto md:-translate-x-1/2 z-50 transition-all duration-500 ${
        isScrolled ? 'glass-strong rounded-2xl md:rounded-full max-w-6xl md:w-[min(96vw,1200px)]' : 'glass rounded-2xl md:rounded-full max-w-6xl md:w-[min(96vw,1200px)]'
      }`}
    >
      <nav className="px-4 md:px-6 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5 group shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_paisley-org-clone/artifacts/6pjbshkc_paisley-logo.webp"
              alt="Paisley.org.uk"
              className="h-9 w-auto"
            />
          </Link>

          {/* Desktop navigation */}
          <div className="hidden lg:flex items-center gap-0.5 min-w-0" ref={dropdownRef}>
            {PRIMARY.map(renderBarItem)}

            <div className="relative">
              <button
                onClick={() => setOpenDropdown(discoverOpen ? null : 'Discover')}
                className={`${BAR_DEFAULT} inline-flex items-center gap-1`}
                data-testid="nav-discover-toggle"
                aria-expanded={discoverOpen}
              >
                Discover
                <ChevronDown size={12} className={`transition-transform duration-200 ${discoverOpen ? 'rotate-180' : ''}`} />
              </button>
              {discoverOpen && (
                <div className="absolute top-full right-0 mt-2 w-60 bg-white rounded-2xl py-2 shadow-[0_16px_48px_rgba(15,23,42,0.16)] ring-1 ring-slate-200 z-50">
                  {/* Items that dropped out of the bar at this width */}
                  <div className="xl:hidden">
                    {COLLAPSED.map((sub) => (
                      <Link key={`c-${sub.url}`} to={sub.url} onClick={closeAll} className={DROP_ITEM} data-testid={`nav-collapsed-${testId(sub.name)}`}>
                        {sub.name}
                      </Link>
                    ))}
                    <div className="my-1.5 mx-4 border-t border-slate-200/70" />
                  </div>
                  {DISCOVER.map((sub) =>
                    sub.external ? (
                      <a
                        key={sub.url}
                        href={sub.url}
                        rel="noopener noreferrer"
                        onClick={closeAll}
                        className={`${DROP_ITEM} inline-flex items-center gap-1.5 w-full`}
                        data-testid={testId(sub.name)}
                      >
                        {sub.name}
                        <ExternalLink size={11} className="text-slate-400" />
                      </a>
                    ) : (
                      <Link key={sub.url} to={sub.url} onClick={closeAll} className={DROP_ITEM} data-testid={testId(sub.name)}>
                        {sub.name}
                      </Link>
                    )
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Search + CTA */}
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            {searchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search…"
                  autoFocus
                  className="px-4 py-1.5 bg-white/70 backdrop-blur border border-slate-200 rounded-full text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-sky-400 w-36 xl:w-44"
                  data-testid="header-search-input"
                />
                <button type="submit" className="ml-1 p-1.5 bg-sky-500 hover:bg-sky-600 text-white rounded-full transition-colors" data-testid="header-search-submit">
                  <Search size={14} />
                </button>
                <button type="button" onClick={() => { setSearchOpen(false); setSearchQuery(''); }} className="ml-1 text-slate-500 hover:text-slate-700">
                  <X size={14} />
                </button>
              </form>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="text-slate-700 hover:text-slate-900 hover:bg-white/50 rounded-full p-2 transition-all"
                data-testid="header-search-toggle"
                aria-label="Search"
              >
                <Search size={16} />
              </button>
            )}
            <Link to="/contact" data-testid="nav-contact">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full font-semibold text-xs h-8 px-4 whitespace-nowrap">
                Contact
              </Button>
            </Link>
            {/* Google Preferred Sources button — injected at runtime by publisher.js */}
            <div google-add-preferred-source-btn="" data-testid="gps-header" className="flex items-center ml-1" />
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center gap-1">
            <button onClick={() => setSearchOpen(!searchOpen)} className="text-slate-700 p-2" aria-label="Search" data-testid="mobile-search-toggle">
              <Search size={18} />
            </button>
            <button
              className="text-slate-700 p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Menu"
              aria-expanded={isMobileMenuOpen}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile search */}
        {searchOpen && (
          <div className="lg:hidden mt-3 px-1">
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles…"
                autoFocus
                className="flex-1 px-4 py-2.5 bg-white/70 backdrop-blur border border-slate-200 rounded-full text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-sky-400"
                data-testid="mobile-search-input"
              />
              <button type="submit" className="px-4 py-2.5 bg-sky-500 hover:bg-sky-600 text-white rounded-full transition-colors" data-testid="mobile-search-submit">
                <Search size={18} />
              </button>
            </form>
          </div>
        )}

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-3 pb-2 pt-3 border-t border-slate-200/60 max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {PRIMARY.map((link) => {
                if (link.dropdown) {
                  return (
                    <div key={link.name} className="border-l-2 border-slate-200 pl-2 my-1">
                      <p className="px-3 py-1 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                        {link.name}
                      </p>
                      {link.dropdown.map((sub) => (
                        <Link key={sub.url} to={sub.url} className={MOBILE_ITEM} onClick={closeAll} data-testid={`mobile-${testId(sub.name)}`}>
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  );
                }
                if (link.accent) {
                  return (
                    <Link
                      key={link.url}
                      to={link.url}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-sky-800 bg-sky-50/70 ring-1 ring-sky-200/70 rounded-xl transition-all"
                      onClick={closeAll}
                      data-testid={`mobile-${testId(link.name)}`}
                    >
                      <LiveDot />
                      {link.name}
                    </Link>
                  );
                }
                return (
                  <Link key={link.url} to={link.url} className={MOBILE_ITEM} onClick={closeAll} data-testid={`mobile-${testId(link.name)}`}>
                    {link.name}
                  </Link>
                );
              })}

              <div className="border-l-2 border-slate-200 pl-2 my-1">
                <p className="px-3 py-1 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                  Discover
                </p>
                {DISCOVER.map((sub) =>
                  sub.external ? (
                    <a key={sub.url} href={sub.url} rel="noopener noreferrer" className={MOBILE_ITEM} onClick={closeAll} data-testid={`mobile-${testId(sub.name)}`}>
                      {sub.name}
                    </a>
                  ) : (
                    <Link key={sub.url} to={sub.url} className={MOBILE_ITEM} onClick={closeAll} data-testid={`mobile-${testId(sub.name)}`}>
                      {sub.name}
                    </Link>
                  )
                )}
              </div>

              <Link to="/contact" onClick={closeAll} className="mt-2" data-testid="mobile-nav-contact">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full font-semibold w-full">
                  Contact
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
